import type {
  ApiError,
  ChatRejection,
  ChatRequestOutcome,
  ChatRequestResponse,
  JoinRoomErrorCode,
} from '@pixelworld/shared';

/** All Danish UI text in one place, so a second language can be added later. */
export const da = {
  appName: 'PixelWorld',
  tagline: 'Spil sammen med dine venner',
  start: {
    hello: 'Hej,',
    playNow: 'Spil nu',
    openRooms: 'Åbne rum',
    noOpenRooms: 'Der er ingen åbne rum lige nu. Tryk på Spil nu for at starte et.',
    roomPlayers: (count: number, max: number) => `${count} af ${max} spillere`,
    enterRoom: 'Gå ind',
    createRoom: 'Opret nyt rum',
    privateRoom: 'Kun med kode (vises ikke på listen)',
    or: 'eller',
    codeLabel: 'Rumkode fra en ven',
    codePlaceholder: 'ABC123',
    joinRoom: 'Deltag',
    players: (min: number, max: number) => `${min}–${max} spillere`,
  },
  auth: {
    loginTitle: 'Log ind',
    usernameLabel: 'Spillernavn',
    passwordLabel: 'Adgangskode',
    loginButton: 'Log ind',
    noAccount: 'Har du ikke en konto endnu?',
    goToRegister: 'Opret konto',
    registerTitle: 'Opret konto',
    registerIntro:
      'Vælg et spillernavn og en adgangskode. Brug ikke dit rigtige navn, din alder eller noget, som andre kan finde dig med.',
    newUsernameLabel: 'Vælg et spillernavn',
    usernamePlaceholder: 'Fx Pixel-Pia',
    usernameHint: '3–16 tegn: bogstaver, tal, - og _. Det er også det, de andre ser.',
    newPasswordLabel: 'Vælg en adgangskode',
    passwordHint: (min: number) =>
      `Mindst ${min} tegn. Tre ord er nemme at huske, fx sol-kage-hest.`,
    repeatPasswordLabel: 'Skriv adgangskoden igen',
    passwordsDiffer: 'De to adgangskoder er ikke ens.',
    registerButton: 'Opret konto',
    haveAccount: 'Har du allerede en konto?',
    goToLogin: 'Log ind',
    logout: 'Log ud',
    offline: 'Vi kan ikke få fat i PixelWorld lige nu.',
    tryAgain: 'Prøv igen',
  },
  authErrors: {
    INVALID_REQUEST: 'Tjek, at alle felter er udfyldt rigtigt.',
    RATE_LIMITED: 'Det var mange forsøg. Vent et par minutter, og prøv igen.',
    INVALID_CREDENTIALS: 'Forkert spillernavn eller adgangskode.',
    ACCOUNT_BLOCKED: 'Din konto er spærret. Spørg en voksen, hvis du tror, det er en fejl.',
    INVITE_INVALID: 'Invitationskoden virker ikke. Den kan være brugt eller for gammel.',
    USERNAME_TAKEN: 'Det spillernavn er taget. Prøv et andet.',
    USERNAME_NOT_ALLOWED: 'Det spillernavn må du ikke bruge. Prøv et andet.',
    UNAUTHORIZED: 'Du skal logge ind først.',
    FORBIDDEN: 'Det har du ikke adgang til.',
    NOT_OWNED: 'Noget af tøjet er ikke dit endnu. Køb det først.',
    NOT_ENOUGH_COINS: 'Du har ikke PixelCoins nok til det endnu.',
    ALREADY_OWNED: 'Det har du allerede.',
    NOT_FOUND: 'Det findes ikke.',
    INTERNAL_ERROR: 'Noget gik galt. Prøv igen om lidt.',
  } satisfies Record<ApiError['error'], string>,
  profile: {
    edit: 'Rediger profil',
    title: 'Din profil',
    back: 'Tilbage',
    loading: 'Henter din profil …',
    loadFailed: 'Din profil kunne ikke hentes.',
    nameTitle: 'Spillernavn',
    nameLabel: 'Det navn, de andre ser',
    nameHint: (username: string) =>
      `3–16 tegn: bogstaver, tal, - og _. Du logger stadig ind som ${username}.`,
    saveName: 'Gem navn',
    nameSaved: 'Dit nye navn er gemt.',
    figureTitle: 'Din figur',
    figure: 'Figur',
    hat: 'Hat',
    noHat: 'Ingen hat',
    face: 'Ansigt',
    noFace: 'Intet',
    look: 'Byg din egen figur',
    saveFigure: 'Gem figur',
    figureSaved: 'Din figur er gemt. Den ses næste gang, du går ind i et rum.',
    shopHint: 'Prøv alt på. Det med et tal koster PixelCoins, som du tjener i spillene.',
    balance: (coins: number) => `${coins} PixelCoins`,
    price: (coins: number) => `koster ${coins} PixelCoins`,
    buy: (name: string, coins: number) => `Køb ${name} · ${coins}`,
    missing: (coins: number) => `Du mangler ${coins} PixelCoins. Spar op ved at spille.`,
    bought: (name: string) => `Værsgo! ${name} er din nu.`,
    preview: 'Sådan ser din figur ud',
  },
  admin: {
    blockTitle: 'Spær en spiller',
    usernameLabel: 'Spillernavn eller navnet i spillet',
    block: 'Spær',
    unblock: 'Genåbn',
    blocked: (name: string) => `${name} er spærret og er smidt ud af spillet.`,
    unblocked: (name: string) => `${name} kan spille igen.`,
    chatTitle: 'Chat',
    chatApprovedBy: (name: string) => `lov fra ${name}`,
    chatRefresh: 'Opdatér',
    chatEmpty: 'Ingen beskeder endnu. Loggen tømmes, når serveren genstarter.',
    chatStopped: 'Stoppet',
    chatLoadFailed: 'Chatloggen kunne ikke hentes.',
    statusTitle: 'Sådan går det',
    statusLine: (players: number, rooms: number) =>
      `${players === 1 ? '1 spiller' : `${players} spillere`} i ${rooms === 1 ? '1 rum' : `${rooms} rum`} lige nu`,
    statusStarted: (since: string) => `Serveren startede ${since}`,
    statusReports: (count: number) =>
      count === 1 ? '1 ny melding fra børnene' : `${count} nye meldinger fra børnene`,
    statusNoErrors: 'Ingen fejl de sidste 7 dage.',
    statusErrors: 'Fejl de sidste 7 dage',
    statusLoadFailed: 'Overblikket kunne ikke hentes.',
    errorSource: { server: 'Server', client: 'Browser' },
    errorCount: (count: number) => (count === 1 ? '1 gang' : `${count} gange`),
    reportsTitle: 'Meldinger fra børnene',
    reportsEmpty: 'Ingen meldinger. De gemmes i 30 dage.',
    reportsLoadFailed: 'Meldingerne kunne ikke hentes.',
    reportLine: (reporter: string, reported: string) => `${reporter} meldte ${reported}`,
    reportNoChat: 'Ingen fri tekst fra spilleren de sidste 10 minutter.',
    reportSeen: 'Set',
    reportMarkSeen: 'Markér som set',
  },
  parents: {
    link: 'Til forældre',
    title: 'Til forældre',
    intro:
      'PixelWorld er en lille spilverden for børn mellem 9 og 13 år, hvor de bygger, fisker og spiller sammen. Her er det vigtigste at vide som forælder.',
    sections: [
      {
        heading: 'Ingen rigtige penge',
        lines: [
          'Spillet er gratis, og der er ingen reklamer.',
          'PixelCoins tjenes kun ved at spille og kan aldrig købes for rigtige penge.',
          'Der er ingen lykkehjul eller præmier, man betaler for at få en chance for.',
        ],
      },
      {
        heading: 'Chat',
        lines: [
          'Overalt kan børnene sende faste beskeder og emojis.',
          'Fri tekst kan kun skrives i rum, man kommer ind i med en kode, eller når den, der startede et åbent rum, siger ja.',
          'Et filter stopper telefonnumre, links, e-mails, brugernavne fra andre apps og grimme ord. Intet filter fanger alt, og derfor findes resten af reglerne her.',
          'En voksen kan læse beskederne og spærre en spiller.',
        ],
      },
      {
        heading: 'Hvis noget er ubehageligt',
        lines: [
          'Barnet kan skjule en spillers beskeder med ét tryk.',
          'Under "Spillere" kan barnet trykke "Sig det til en voksen". Så får vi det at vide, og spilleren bliver skjult for barnet.',
          'Tal gerne med dit barn om, at de aldrig skal give navn, skole eller adresse til nogen i et spil.',
        ],
      },
      {
        heading: 'Venner',
        lines: [
          'Børnene kan kun blive venner med en, de spiller med lige nu, og begge skal sige ja.',
          'Venner kan se, om den anden spiller, men aldrig rumkoden. I et rum med kode skal vennen derinde lukke én ind.',
          'Der er kun faste ord, ingen fri tekst. Et "nej tak" får den anden ikke at vide.',
          'Melder barnet en ven til en voksen, er de ikke venner længere, og ingen af dem kan spørge igen.',
        ],
      },
      {
        heading: 'Hvad vi gemmer',
        lines: [
          'Et brugernavn, et spillernavn og adgangskoden, som kun gemmes krypteret. Vi beder ikke om e-mail, rigtigt navn eller alder.',
          'Barnets figur, PixelCoins og fremskridt i spillet.',
          'Hvem barnet er venner med, og venneanmodninger, der venter på svar (de slettes efter 7 dage).',
          'Fri tekst gemmes ikke. Kun når et barn melder en spiller, gemmes spillerens seneste beskeder sammen med meldingen i 30 dage.',
          'Vi bruger kun én nødvendig cookie, der holder barnet logget ind. Ingen sporing og ingen statistik-cookies.',
          'Serveren og databasen står i EU (Amsterdam).',
        ],
      },
    ],
    back: 'Tilbage',
  },
  friends: {
    title: 'Venner',
    empty:
      'Du har ingen venner her endnu. Når du spiller med nogen, kan du trykke "Bliv venner" under Spillere.',
    offline: 'Ikke online',
    onStart: 'På forsiden',
    playing: (game: string, withCode: boolean) =>
      withCode ? `${game} · rum med kode` : `${game} · åbent rum`,
    goTo: (name: string) => `Gå til ${name}`,
    knock: 'Bank på',
    remove: 'Fjern',
    removeAsk: (name: string) => `Fjern ${name} som ven?`,
    removeYes: 'Ja, fjern',
    cancel: 'Fortryd',
    requestFrom: (name: string) => `${name} vil gerne være venner`,
    yes: 'Ja',
    noThanks: 'Nej tak',
    inviteFrom: (name: string) => `${name} vil gerne spille med dig`,
    goWith: 'Gå med',
    knockFrom: (name: string) => `${name} banker på. Vil du lukke ${name} ind?`,
    letIn: 'Luk ind',
    notNow: 'Ikke nu',
    befriend: 'Bliv venner',
    asked: 'Spurgt',
    isFriend: 'Ven',
    askErrors: {
      tooManyFriends: 'Du har allerede 50 venner.',
      tooManyRequests: 'Vent på svar, før du spørger flere.',
      failed: 'Det virkede ikke. Prøv igen.',
    },
    elsewhere: 'Venner andre steder',
    callOver: 'Kom og spil',
    callOutcome: {
      sent: 'Sendt!',
      tooSoon: 'Vent lidt, før du kalder igen.',
      failed: 'Det virkede ikke.',
    },
  },
  report: {
    button: 'Sig det til en voksen',
    ask: (name: string) =>
      `Var ${name} ikke sød? Vi siger det til en voksen og skjuler ${name}s beskeder for dig.`,
    confirm: 'Ja, sig det',
    cancel: 'Fortryd',
    sending: 'Sender …',
    sent: (name: string) => `Tak. En voksen kigger på det. Du ser ikke ${name}s beskeder mere.`,
    failed: 'Det virkede ikke. Prøv igen om lidt, eller sig det til en voksen derhjemme.',
  },
  chat: {
    label: 'Skriv til dine venner',
    placeholder: 'Skriv til dine venner …',
    send: 'Send',
    open: 'Skriv en besked',
    mute: 'Skjul',
    unmute: 'Vis igen',
    mutedNote: (name: string) => `Du har skjult ${name}s beskeder.`,
    errors: {
      notAllowed: 'Spørg værten, om du må skrive frit.',
      rateLimited: 'Vent lidt, før du skriver igen.',
      empty: 'Skriv noget først.',
      tooLong: 'Beskeden er for lang. Højst 60 tegn.',
      contact:
        'Beskeden blev ikke sendt. Skriv ikke telefonnumre, links, adresser eller navne fra andre apps.',
      language: 'Beskeden blev ikke sendt. Skriv pænt til hinanden.',
      INVALID_REQUEST: 'Beskeden kunne ikke sendes. Prøv igen.',
    } satisfies Record<ChatRejection | 'INVALID_REQUEST', string>,
    ask: 'Spørg om at skrive frit',
    waiting: 'Venter på svar fra værten …',
    requestErrors: {
      notNeeded: 'Du må allerede skrive.',
      pending: 'Du har allerede spurgt. Vent på svar.',
      tooSoon: 'Vent lidt, før du spørger igen.',
      INVALID_REQUEST: 'Det virkede ikke. Prøv igen.',
    } satisfies Record<Extract<ChatRequestResponse, { ok: false }>['error'], string>,
    answers: {
      allowed: 'Værten sagde ja! Nu kan du skrive.',
      denied: 'Værten sagde nej. Du kan spørge igen om lidt.',
      expired: 'Værten svarede ikke. Du kan spørge igen.',
      revoked: 'Værten har taget din tilladelse tilbage.',
    } satisfies Record<ChatRequestOutcome, string>,
    request: (name: string) => `${name} vil gerne skrive frit.`,
    allow: 'Ja',
    deny: 'Nej',
  },
  wallet: {
    title: 'Dine PixelCoins. Du tjener dem i spillene.',
    balance: (coins: number) => `${coins} PixelCoins`,
    gained: (coins: number) => (coins < 0 ? `−${-coins}` : `+${coins}`),
  },
  play: {
    connecting: 'Forbinder til øen …',
    goingToFriend: 'Går hen til din ven …',
    loadingGame: 'Pakker rygsækken …',
    roomCode: 'Rumkode',
    copy: 'Kopiér',
    copied: 'Kopieret!',
    leave: 'Forlad rum',
    players: (count: number) => (count === 1 ? '1 spiller' : `${count} spillere`),
    playerList: 'Spillere i rummet',
    host: 'Vært',
    mayWrite: 'Må skrive',
    revoke: 'Fjern lov',
    keyboardHint: 'Gå med WASD eller piletasterne',
    touchHint: 'Sæt en finger på skærmen og træk for at gå',
    disconnected: 'Forbindelsen til serveren blev afbrudt.',
    reconnecting: 'Forbindelsen røg. Vi prøver igen …',
    replaced: 'Du spiller nu i et andet vindue eller på en anden enhed.',
    updating: 'PixelWorld bliver opdateret. Vi er straks tilbage …',
    backToStart: 'Tilbage til start',
    unknownGame: 'Det spil findes ikke.',
    gameLoadFailed: 'Spillet kunne ikke indlæses. Prøv at genindlæse siden.',
  },
  joinErrors: {
    INVALID_REQUEST: 'Tjek rumkoden.',
    RATE_LIMITED: 'Vent lidt, og prøv igen.',
    UNKNOWN_GAME: 'Det spil findes ikke.',
    ROOM_NOT_FOUND: 'Vi kan ikke finde et rum med den kode. Tjek, at den er skrevet rigtigt.',
    ROOM_FULL: 'Rummet er fuldt. Der kan højst være 6 spillere.',
    NOT_FRIENDS: 'I er ikke venner længere.',
    FRIEND_NOT_PLAYING: 'Din ven spiller ikke lige nu.',
    KNOCK_REFUSED: 'Din ven kunne ikke lukke dig ind lige nu.',
  } satisfies Record<JoinRoomErrorCode, string>,
  notFound: 'Den side findes ikke.',
} as const;
